import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Badge } from 'react-bootstrap';
import {
    FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaVenusMars,
    FaSave, FaTimes, FaTrash, FaKey, FaUserShield, FaGlobeAmericas,
    FaInfoCircle, FaUserPlus, FaLock, FaEye, FaEyeSlash
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../assets/css/CreateUserModal.css';
import RoleService from '../../../services/RoleService';
import HabilitationService from '../../../services/HabilitationService';
import UserService from '../../../services/UserService';
import PhoneNumberInput from "../../../utils/PhoneNumberInput";
import CountryInput from "../../../utils/CountryInput";
import CityInput from "../../../utils/AdminCityInput";
import NeighborhoodInput from "../../../utils/NeighborhoodInput";
import LoadingSpinner from '../../../utils/LoadingSpinner';

const FieldBox = ({ label, icon, children }) => (
    <div className="mb-3">
        <div className="d-flex align-items-center mb-2 text-secondary fw-semibold" style={{ fontSize: '0.85rem' }}>
            <span className="me-2 d-flex">{icon}</span>{label}
        </div>
        <div className="border rounded-3 px-3 py-2 bg-white" style={{ minHeight: '42px' }}>
            {children}
        </div>
    </div>
);

const Section = ({ title, icon, color, children }) => (
    <div className="rounded-3 p-4 mb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <h5 className="fw-bold mb-4 d-flex align-items-center" style={{ color }}>
            <span className="me-2 d-flex">{icon}</span>{title}
        </h5>
        {children}
    </div>
);

// Fonction utilitaire pour vérifier les règles du mot de passe
const getPasswordErrors = (password) => {
    const errs = [];
    if (!password || password.length < 6) errs.push('Au moins 6 caractères');
    if (!password || !/[a-z]/.test(password)) errs.push('Au moins une lettre minuscule');
    if (!password || !/[A-Z]/.test(password)) errs.push('Au moins une lettre majuscule');
    if (!password || !/[0-9]/.test(password)) errs.push('Au moins un chiffre');
    if (!password || !/[^a-zA-Z0-9]/.test(password)) errs.push('Au moins un caractère spécial');
    return errs;
};

const CreateUserModal = ({ onClose, onCreate }) => {
    const [newUser, setNewUser] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        neighborhood: '',
        city: '',
        country: '',
        dateOfBirth: '',
        gender: '',
        username: '',
        password: '',
        confirmPassword: '',
        profilePictureUrl: '',
        additionalInfos: '',
        roleIds: [],
        habilitationNames: []
    });

    const [allRoles, setAllRoles] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [allHabilitations, setAllHabilitations] = useState([]);
    const [availableHabilitations, setAvailableHabilitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [formSubmitted, setFormSubmitted] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [rolesResponse, habilitationsResponse] = await Promise.all([
                    RoleService.findAllRoles(),
                    HabilitationService.getAllHabilitations()
                ]);

                setAllRoles(rolesResponse);
                setAvailableRoles(rolesResponse);
                setAllHabilitations(habilitationsResponse);
                setAvailableHabilitations(habilitationsResponse);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Erreur lors du chargement des données initiales');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const validateField = (name, value, currentState) => {
        let error = '';
        const stateToCheck = currentState || newUser;

        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'email':
                if (!value.trim()) {
                    error = 'Ce champ est obligatoire';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Email invalide';
                }
                break;
            case 'phoneNumber':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            case 'username':
            case 'neighborhood':
            case 'city':
            case 'country':
                if (!value || !value.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'dateOfBirth':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                } else {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const birthDate = new Date(value);
                    if (birthDate > today) {
                        error = 'La date ne peut pas être dans le futur';
                    }
                }
                break;
            case 'gender':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            case 'password':
                // Retourne un tableau d'erreurs pour le mot de passe
                return getPasswordErrors(value);
            case 'confirmPassword':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                } else if (value !== stateToCheck.password) {
                    error = 'Les mots de passe ne correspondent pas';
                }
                break;
            default:
                break;
        }

        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedUser = { ...newUser, [name]: value };

        const error = validateField(name, value, updatedUser);
        const newErrors = { ...errors, [name]: error };

        if (name === 'password') {
            const confirmError = validateField('confirmPassword', updatedUser.confirmPassword, updatedUser);
            newErrors.confirmPassword = confirmError;
        }

        setErrors(newErrors);
        setNewUser(updatedUser);
    };

    // --- Handlers pour les composants réutilisables ---
    const handlePhoneNumberChange = (value) => {
        setNewUser(prev => ({ ...prev, phoneNumber: value }));
        setErrors(prev => ({ ...prev, phoneNumber: null }));
    };

    const handlePhoneValidation = (error) => {
        setErrors(prev => ({ ...prev, phoneNumber: error }));
    };

    const handleCountryChange = (value) => {
        setNewUser(prev => ({ ...prev, country: value }));
        setErrors(prev => ({ ...prev, country: null }));
    };

    const handleCountryValidation = (error) => {
        setErrors(prev => ({ ...prev, country: error }));
    };

    const handleCityChange = (value) => {
        setNewUser(prev => ({ ...prev, city: value }));
        setErrors(prev => ({ ...prev, city: null }));
    };

    const handleCityValidation = (error) => {
        setErrors(prev => ({ ...prev, city: error }));
    };

    const handleNeighborhoodChange = (value) => {
        setNewUser(prev => ({ ...prev, neighborhood: value }));
        setErrors(prev => ({ ...prev, neighborhood: null }));
    };

    const handleNeighborhoodValidation = (error) => {
        setErrors(prev => ({ ...prev, neighborhood: error }));
    };

    const handleRoleSelect = (e) => {
        const selectedRoleId = parseInt(e.target.value);
        if (selectedRoleId === 0) return;

        const selectedRole = allRoles.find(role => role.id === selectedRoleId);

        if (selectedRole && !newUser.roleIds.includes(selectedRoleId)) {
            setNewUser(prev => ({
                ...prev,
                roleIds: [...prev.roleIds, selectedRoleId]
            }));

            setAvailableRoles(prev =>
                prev.filter(role => role.id !== selectedRoleId)
            );

            setErrors(prev => ({
                ...prev,
                roles: undefined
            }));
        }
    };

    const removeRole = (roleId) => {
        setNewUser(prev => ({
            ...prev,
            roleIds: prev.roleIds.filter(id => id !== roleId)
        }));

        const roleToAddBack = allRoles.find(role => role.id === roleId);
        if (roleToAddBack) {
            setAvailableRoles(prev => [...prev, roleToAddBack]);
        }
    };

    const handleHabilitationSelect = (e) => {
        const selectedHabilitationName = e.target.value;
        if (selectedHabilitationName === "") return;

        const selectedHabilitation = allHabilitations.find(
            h => h.name === selectedHabilitationName
        );

        if (selectedHabilitation && !newUser.habilitationNames.includes(selectedHabilitationName)) {
            setNewUser(prev => ({
                ...prev,
                habilitationNames: [...prev.habilitationNames, selectedHabilitationName]
            }));

            setAvailableHabilitations(prev =>
                prev.filter(h => h.name !== selectedHabilitationName)
            );
        }
    };

    const removeHabilitation = (habilitationName) => {
        setNewUser(prev => ({
            ...prev,
            habilitationNames: prev.habilitationNames.filter(name => name !== habilitationName)
        }));

        const habilitationToAddBack = allHabilitations.find(h => h.name === habilitationName);
        if (habilitationToAddBack) {
            setAvailableHabilitations(prev => [...prev, habilitationToAddBack]);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(newUser).forEach(field => {
            if (!['profilePictureUrl', 'additionalInfos', 'habilitationNames', 'phoneNumber', 'country', 'city', 'neighborhood'].includes(field)) {
                const error = validateField(field, newUser[field], newUser);
                // Gestion spécifique pour le tableau d'erreurs du mot de passe
                if (field === 'password') {
                    if (error.length > 0) {
                        newErrors[field] = error;
                        isValid = false;
                    }
                } else if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            }
        });

        if (errors.phoneNumber) { newErrors.phoneNumber = errors.phoneNumber; isValid = false; }
        if (errors.country) { newErrors.country = errors.country; isValid = false; }
        if (errors.city) { newErrors.city = errors.city; isValid = false; }
        if (errors.neighborhood) { newErrors.neighborhood = errors.neighborhood; isValid = false; }

        if (newUser.roleIds.length === 0) {
            newErrors.roles = 'Au moins un rôle doit être sélectionné';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        setFormSubmitted(true);

        if (!validateForm()) {
            const firstErrorField = Object.keys(errors).find(key => {
                if (key === 'password') return errors[key].length > 0;
                return errors[key];
            });
            if (firstErrorField) {
                const element = document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.focus();
                } else if (firstErrorField === 'roles') {
                    document.querySelector('.roles-section select')?.focus();
                }
            }
            return;
        }

        setSubmitting(true);

        try {
            let formattedDateOfBirth = '';
            if (newUser.dateOfBirth) {
                const date = new Date(newUser.dateOfBirth);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                formattedDateOfBirth = `${day}-${month}-${year} 00:00`;
            }

            const { confirmPassword, ...userDataToSend } = newUser;

            const userWithAdditionalFields = {
                ...userDataToSend,
                dateOfBirth: formattedDateOfBirth || null,
                createdBy: 'admin',
                lastModifiedBy: 'admin',
            };

            const createdUser = await UserService.createUser(userWithAdditionalFields);

            toast.success('Utilisateur créé avec succès');
            onCreate(createdUser);
            onClose();
        } catch (error) {
            console.error('Error creating user:', error);

            let errorMessage = 'Erreur lors de la création de l\'utilisateur';
            if (error.response) {
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 404) {
                    errorMessage = 'Endpoint non trouvé - Vérifiez l\'URL de l\'API';
                } else if (error.response.data) {
                    errorMessage = JSON.stringify(error.response.data);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (label, value, icon, fieldName, type = 'text') => {
        const error = errors[fieldName];

        return (
            <FieldBox label={label} icon={icon}>
                {type === 'textarea' ? (
                    <textarea
                        name={fieldName}
                        value={value}
                        onChange={handleInputChange}
                        className={`form-control form-control-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                        placeholder={`Entrez ${label.toLowerCase()}`}
                        disabled={submitting}
                        rows={3}
                    />
                ) : (
                    <input
                        type={type}
                        name={fieldName}
                        value={value}
                        onChange={handleInputChange}
                        className={`form-control form-control-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                        placeholder={`Entrez ${label.toLowerCase()}`}
                        disabled={submitting}
                    />
                )}
                {error && <div className="invalid-feedback d-block small">{error}</div>}
            </FieldBox>
        );
    };

    if (loading) {
        return (
            <div className="user-modal-overlay">
                <div
                    className="user-modal-content"
                    style={{
                        borderTopLeftRadius: '20px',
                        borderTopRightRadius: '20px',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '300px'
                    }}
                >
                    <LoadingSpinner
                        isVisible={true}
                        loadingText="Chargement des données..."
                        position="absolute"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="user-modal-overlay">
            <div
                className="user-modal-content"
                style={{
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    overflow: 'hidden'
                }}
            >
                {/* En-tête */}
                <div className="d-flex justify-content-between align-items-start px-4 py-3 border-bottom bg-white flex-shrink-0">
                    <div className="d-flex align-items-start">
                        <FaUserPlus size={22} className="text-primary me-3 mt-1" />
                        <div>
                            <h4 className="fw-bold mb-0">Créer un nouvel utilisateur</h4>
                            <small className="text-muted">Remplissez les informations ci-dessous</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button variant="success" size="sm" onClick={handleSubmit} disabled={submitting}>
                            <FaSave className="me-1" /> {submitting ? 'Création en cours...' : 'Créer'}
                        </Button>
                        <FaTimes
                            size={22}
                            role="button"
                            className="text-secondary ms-2"
                            onClick={!submitting ? onClose : undefined}
                            style={{ opacity: submitting ? 0.5 : 1, pointerEvents: submitting ? 'none' : 'auto' }}
                        />
                    </div>
                </div>

                {/* Corps scrollable */}
                <div className="user-modal-body">
                    <div className="container-fluid">
                        {/* Informations personnelles */}
                        <Section title="Informations Personnelles" icon={<FaUser />} color="#6f42c1">
                            <Row>
                                <Col md={6}>{renderField('Prénom', newUser.firstName, <FaUser />, 'firstName')}</Col>
                                <Col md={6}>{renderField('Nom', newUser.lastName, <FaUser />, 'lastName')}</Col>
                                <Col md={6}>{renderField('Email', newUser.email, <FaEnvelope />, 'email', 'email')}</Col>
                                <Col md={6}>{renderField('Date de naissance', newUser.dateOfBirth, <FaCalendarAlt />, 'dateOfBirth', 'date')}</Col>
                                <Col md={6}>
                                    <FieldBox label="Genre" icon={<FaVenusMars />}>
                                        <select
                                            name="gender"
                                            value={newUser.gender}
                                            onChange={handleInputChange}
                                            className={`form-select form-select-sm border-0 p-0 ${errors.gender ? 'is-invalid' : ''}`}
                                            disabled={submitting}
                                        >
                                            <option value="">Sélectionnez un genre</option>
                                            <option value="MALE">Masculin</option>
                                            <option value="FEMALE">Féminin</option>
                                        </select>
                                        {errors.gender && (
                                            <div className="invalid-feedback d-block small">{errors.gender}</div>
                                        )}
                                    </FieldBox>
                                </Col>
                            </Row>
                        </Section>

                        {/* Informations de connexion */}
                        <Section title="Informations de Connexion" icon={<FaKey />} color="#0d6efd">
                            <Row>
                                <Col md={6}>{renderField('Username', newUser.username, <FaUser />, 'username')}</Col>

                                {/* Champ Mot de passe avec œil */}
                                <Col md={6}>
                                    <FieldBox label="Mot de passe" icon={<FaLock />}>
                                        <div className="d-flex align-items-center w-100">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={newUser.password}
                                                onChange={handleInputChange}
                                                className={`form-control form-control-sm border-0 p-0 ${errors.password && errors.password.length > 0 ? 'is-invalid' : ''}`}
                                                placeholder="Entrez le mot de passe"
                                                disabled={submitting}
                                            />
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-secondary p-0 ms-2"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={submitting}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </div>
                                        {/* Affichage dynamique de la liste des règles non satisfaites */}
                                        {errors.password && errors.password.length > 0 && (
                                            <div className="invalid-feedback d-block small">
                                                <ul className="mb-0 ps-3">
                                                    {errors.password.map((err, idx) => (
                                                        <li key={idx}>{err}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </FieldBox>
                                </Col>

                                {/* Champ Confirmation Mot de passe avec œil */}
                                <Col md={6}>
                                    <FieldBox label="Confirmer le mot de passe" icon={<FaLock />}>
                                        <div className="d-flex align-items-center w-100">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={newUser.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`form-control form-control-sm border-0 p-0 ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                placeholder="Confirmez le mot de passe"
                                                disabled={submitting}
                                            />
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-secondary p-0 ms-2"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                disabled={submitting}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </div>
                                        {errors.confirmPassword && <div className="invalid-feedback d-block small">{errors.confirmPassword}</div>}
                                    </FieldBox>
                                </Col>
                            </Row>
                        </Section>

                        {/* Localisation */}
                        <Section title="Localisation" icon={<FaMapMarkerAlt />} color="#20c997">
                            <Row className="g-3">
                                <Col md={6}>
                                    <CountryInput
                                        value={newUser.country}
                                        onChange={handleCountryChange}
                                        onValidation={handleCountryValidation}
                                        disabled={submitting}
                                        required={true}
                                    />
                                </Col>

                                <Col md={6}>
                                    <CityInput
                                        value={newUser.city}
                                        onChange={handleCityChange}
                                        onValidation={handleCityValidation}
                                        disabled={submitting}
                                        required={true}
                                    />
                                </Col>

                                <Col md={6}>
                                    <NeighborhoodInput
                                        value={newUser.neighborhood}
                                        onChange={handleNeighborhoodChange}
                                        onValidation={handleNeighborhoodValidation}
                                        disabled={submitting}
                                        required={true}
                                    />
                                </Col>

                                <Col md={6}>
                                    <div className="phone-input-container">
                                        <PhoneNumberInput
                                            value={newUser.phoneNumber}
                                            onChange={handlePhoneNumberChange}
                                            onValidation={handlePhoneValidation}
                                            disabled={submitting}
                                            defaultCountry="CM"
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Section>

                        {/* Rôles et habilitations */}
                        <Section title="Rôles et Habilitations" icon={<FaUserShield />} color="#dc3545">
                            <div className="mb-4 roles-section">
                                <h6 className="fw-semibold mb-2">Rôles</h6>
                                {errors.roles && newUser.roleIds.length === 0 && (
                                    <div className="alert alert-danger py-2">{errors.roles}</div>
                                )}
                                <select
                                    onChange={handleRoleSelect}
                                    className={`form-select mb-3 ${errors.roles ? 'is-invalid' : ''}`}
                                    defaultValue={0}
                                    disabled={submitting || availableRoles.length === 0}
                                >
                                    <option value={0}>Sélectionnez un rôle</option>
                                    {availableRoles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.name} - {role.description}
                                        </option>
                                    ))}
                                </select>
                                <div className="d-flex flex-wrap gap-2">
                                    {newUser.roleIds.map(roleId => {
                                        const role = allRoles.find(r => r.id === roleId);
                                        return role ? (
                                            <Badge key={roleId} bg="primary" className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill">
                                                {role.name}
                                                <FaTrash
                                                    size={11}
                                                    role="button"
                                                    onClick={() => !submitting && removeRole(roleId)}
                                                />
                                            </Badge>
                                        ) : null;
                                    })}
                                </div>
                            </div>

                            <div>
                                <h6 className="fw-semibold mb-2">Habilitations</h6>
                                <select
                                    onChange={handleHabilitationSelect}
                                    className="form-select mb-3"
                                    defaultValue=""
                                    disabled={submitting || availableHabilitations.length === 0}
                                >
                                    <option value="">Sélectionnez une habilitation</option>
                                    {availableHabilitations.map(habilitation => (
                                        <option key={habilitation.name} value={habilitation.name}>
                                            {habilitation.name} ({habilitation.type} - {habilitation.action})
                                        </option>
                                    ))}
                                </select>
                                <div className="d-flex flex-wrap gap-2">
                                    {newUser.habilitationNames.map(name => (
                                        <Badge key={name} bg="secondary" className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill">
                                            {name}
                                            <FaTrash
                                                size={11}
                                                role="button"
                                                onClick={() => !submitting && removeHabilitation(name)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Section>

                        {/* Autres informations */}
                        <Section title="Autres Informations" icon={<FaInfoCircle />} color="#fd7e14">
                            <Row>
                                <Col md={12}>{renderField('Informations supplémentaires', newUser.additionalInfos, <FaUser />, 'additionalInfos', 'textarea')}</Col>
                            </Row>
                        </Section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateUserModal;